import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Truck, Camera, MapPin, AlertTriangle, FileText, Activity, Check, Zap, Pencil, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { CityPicker } from '@/components/CityPicker';
import { WasikaModal, WasikaBanner } from '@/pages/wasika/WasikaModal';
import { RecordCollectionModal } from '@/pages/cashflow/RecordCollectionModal';
import { ReassignSheet } from '@/pages/flatbed/ReassignSheet';
import { DocumentsTab } from '@/components/flatbed/DocumentsTab';
import { InlineEditField, TextEditor, NumberEditor, SelectEditor, DateTimeEditor } from '@/components/common/InlineEditField';
import { EntityLink } from '@/components/common/EntityLink';
import { EntityPicker } from '@/components/common/EntityPicker';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

const STAGES = ['quoted','assigned','en_route_to_pickup','at_pickup','loaded','in_transit','at_delivery','unloaded','returning','completed','invoiced','paid'];

const NEXT_STATUS_AFTER: Record<string, string | null> = {
  quoted: null,
  assigned: 'en_route_to_pickup',
  en_route_to_pickup: 'at_pickup',
  at_pickup: 'loaded',
  loaded: 'in_transit',
  in_transit: 'at_delivery',
  at_delivery: 'unloaded',
  unloaded: 'completed',
  returning: 'completed',
  completed: null,
  invoiced: null,
  paid: null,
};

interface Trip {
  id: string; trip_code: string; status: string;
  // PR-D1 MAY 11 — ids for EntityLink / EntityPicker
  client_id: string | null; vehicle_id: string | null; driver_id: string | null;
  client_name: string | null; client_reference: string | null;
  pickup_city: string; pickup_location_name: string | null;
  delivery_city: string; delivery_location_name: string | null;
  expected_distance_km: number | null; rate_sar: string;
  container_type: string | null; container_number: string | null; cargo_description: string | null;
  vehicle_plate: string | null; driver_name: string | null; driver_phone: string | null;
  scheduled_pickup_at: string | null; scheduled_delivery_at: string | null;
  pickup_completed_at: string | null; delivery_completed_at: string | null; trip_completed_at: string | null;
  driver_advance_sar: string | null; driver_incentive_sar: string | null;
  driver_incentive_released: boolean | null;
  invoiced_at: string | null; invoice_number: string | null;
  expected_payment_date: string | null; paid_at: string | null;
  payment_received_sar: string | null;
  // PR MAY10 P4A.3 — payment tracking columns
  payment_status: string | null; payment_method: string | null; payment_due_date: string | null;
  payer_name: string | null; payer_contact: string | null;
  collector_user_id: string | null; collector_driver_id: string | null;
  amount_paid_sar: string | null; payment_notes: string | null;
  internal_notes: string | null; cancellation_reason: string | null;
  // PR-F3 / PR-S1 MAY 11 — document slot URLs + force-advance audit
  pickup_truck_photo_url?: string | null;
  pickup_receipt_photo_url?: string | null;
  wasika_pdf_url?: string | null;
  delivery_truck_photo_url?: string | null;
  delivery_document_photo_url?: string | null;
  pickup_photos_uploaded_at?: string | null;
  wasika_uploaded_at?: string | null;
  delivery_photos_uploaded_at?: string | null;
  force_advance_audit?: Array<{ fromState: string; toState: string; reason: string; byUserId: string; atTs: string }> | null;
  // PR-D3 MAY 11 — fuel + odometer + computed columns
  odometer_at_pickup_km?: number | null;
  odometer_at_delivery_km?: number | null;
  fuel_filled_at_pickup_liters?: string | null;
  fuel_price_at_pickup_sar?: string | null;
  fuel_topped_up_liters?: string | null;
  fuel_price_at_delivery_sar?: string | null;
  pickup_recorded_at?: string | null;
  delivery_recorded_at?: string | null;
  distance_km?: number | null;
  total_fuel_liters?: string | null;
  total_fuel_cost_sar?: string | null;
  consumption_l_per_100km?: string | null;
}

const PAYMENT_METHODS = ['cash_on_delivery', 'cash_on_pickup', 'bank_transfer', 'cheque', 'credit_15d', 'credit_30d', 'credit_45d', 'credit_60d', 'credit_90d', 'other'];
const PAYMENT_STATUSES = ['unset', 'pending', 'partially_paid', 'paid', 'overdue', 'disputed', 'written_off'];
const CONTAINER_TYPES = ['40ft', '20ft', 'lowbed', 'flatbed-only', 'other'];

interface Stage { id: string; from_status: string | null; to_status: string; changed_at: string; gps_lat: string | null; gps_lng: string | null; notes: string | null }
interface Doc   { id: string; document_type: string; mime_type: string | null; uploaded_at: string; gps_lat: string | null; gps_lng: string | null; description: string | null }
interface WasteAnomaly { id: string; anomaly_type: string; severity: string; title: string; description: string | null; impact_sar: number | null; status: string; detected_at: string }
interface WasteBundle {
  consumption: Record<string, unknown> | null;
  idle:        Record<string, unknown> | null;
  route:       Record<string, unknown> | null;
  anomalies:   WasteAnomaly[];
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function FlatbedTripDetailPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [trip, setTrip] = useState<Trip | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'overview' | 'documents' | 'financials' | 'analytics' | 'activity' | 'anti_waste'>('overview');
  const [transitioning, setTransitioning] = useState(false);
  const [waste, setWaste] = useState<WasteBundle | null>(null);
  const [wasteLoading, setWasteLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  // PR MAY10 P4A.2: admin inline edit mode + edit buffer
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  // PR MAY10 P4B.2: wasika modal + status from /api/v1/wasika
  const [wasikaOpen, setWasikaOpen] = useState(false);
  const [wasikaStatus, setWasikaStatus] = useState<string | null>(null);
  // PR MAY10 P4B-POLISH (A.1): record collection modal + collections list
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [collections, setCollections] = useState<Array<{ id: string; code: string; amount_sar: string; collected_at: string; payment_method: string; collected_by_user_name: string | null; collected_by_driver_name: string | null; verified_at: string | null }>>([]);
  // PR-F4 MAY 11 — reassign sheet
  const [reassignOpen, setReassignOpen] = useState(false);
  const setEditField = (k: string, v: unknown) => setEdit(prev => ({ ...prev, [k]: v }));

  function startEdit() {
    if (!trip) return;
    setEdit({
      clientReference: trip.client_reference,
      pickupCity: trip.pickup_city,
      pickupLocationName: trip.pickup_location_name,
      deliveryCity: trip.delivery_city,
      deliveryLocationName: trip.delivery_location_name,
      expectedDistanceKm: trip.expected_distance_km,
      rateSar: Number(trip.rate_sar),
      containerType: trip.container_type,
      containerNumber: trip.container_number,
      cargoDescription: trip.cargo_description,
      scheduledPickupAt: trip.scheduled_pickup_at ? trip.scheduled_pickup_at.slice(0, 16) : '',
      scheduledDeliveryAt: trip.scheduled_delivery_at ? trip.scheduled_delivery_at.slice(0, 16) : '',
      driverAdvanceSar: trip.driver_advance_sar ? Number(trip.driver_advance_sar) : 0,
      driverIncentiveSar: trip.driver_incentive_sar ? Number(trip.driver_incentive_sar) : null,
      paymentStatus: trip.payment_status ?? 'unset',
      paymentMethod: trip.payment_method,
      paymentDueDate: trip.payment_due_date ? trip.payment_due_date.slice(0, 10) : '',
      payerName: trip.payer_name,
      payerContact: trip.payer_contact,
      amountPaidSar: trip.amount_paid_sar ? Number(trip.amount_paid_sar) : 0,
      paymentNotes: trip.payment_notes,
      internalNotes: trip.internal_notes,
    });
    setEditing(true);
  }
  function cancelEdit() { setEditing(false); setEdit({}); }

  // PR-D1 MAY 11 — single-field patch helper for InlineEditField. Throws on
  // failure so the InlineEditField can surface the error to the user.
  async function patchField(field: string, value: unknown) {
    if (!trip) return;
    const payload: Record<string, unknown> = { [field]: value };
    await apiClient.patch('/api/v1/flatbed/trips/' + trip.id, payload);
    await refresh();
  }

  async function saveEdit() {
    if (!trip) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(edit)) {
        if (k === 'scheduledPickupAt' || k === 'scheduledDeliveryAt') {
          payload[k] = v ? new Date(v as string).toISOString() : null;
        } else if (typeof v === 'string' && v === '') {
          payload[k] = null;
        } else {
          payload[k] = v;
        }
      }
      await apiClient.patch('/api/v1/flatbed/trips/' + trip.id, payload);
      setEditing(false);
      setEdit({});
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const refresh = useCallback(async () => {
    if (!tripId) return;
    try {
      const r = await apiClient.get<{ trip: Trip; stages: Stage[]; documents: Doc[] }>('/api/v1/flatbed/trips/' + tripId);
      setTrip(r.data.trip);
      setStages(r.data.stages);
      setDocs(r.data.documents);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { refresh(); }, [refresh]);

  // PR MAY10 P4B.2 — pull wasika status for banner
  const refreshWasika = useCallback(async () => {
    if (!tripId) return;
    try {
      const r = await apiClient.get<{ items: Array<{ status: string }> }>(`/api/v1/wasika?flatbedTripId=${tripId}`);
      setWasikaStatus(r.data.items[0]?.status ?? null);
    } catch { setWasikaStatus(null); }
  }, [tripId]);
  useEffect(() => { refreshWasika(); }, [refreshWasika]);

  // PR MAY10 P4B-POLISH (A.1): pull collections list for Financials tab
  const refreshCollections = useCallback(async () => {
    if (!tripId) return;
    try {
      const r = await apiClient.get<typeof collections>(`/api/v1/payment-collections?flatbedTripId=${tripId}`);
      setCollections(Array.isArray(r.data) ? r.data : []);
    } catch { /* leave previous */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId]);
  useEffect(() => { refreshCollections(); }, [refreshCollections]);

  useEffect(() => {
    if (tab !== 'anti_waste' || !tripId || waste || wasteLoading) return;
    setWasteLoading(true);
    apiClient.get<WasteBundle>('/api/v1/flatbed-waste/trips/' + tripId)
      .then((r) => setWaste(r.data))
      .catch(() => { /* leave null; user can run analysis */ })
      .finally(() => setWasteLoading(false));
  }, [tab, tripId, waste, wasteLoading]);

  async function runWasteAnalysis() {
    if (!tripId) return;
    setAnalyzing(true);
    try {
      const r = await apiClient.post<WasteBundle>('/api/v1/flatbed-waste/trips/' + tripId + '/analyze', {});
      setWaste({ ...r.data, anomalies: waste?.anomalies ?? [] });
      // Refetch full bundle (with anomalies) after analysis writes
      const b = await apiClient.get<WasteBundle>('/api/v1/flatbed-waste/trips/' + tripId);
      setWaste(b.data);
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setAnalyzing(false);
    }
  }

  async function transition() {
    if (!trip) return;
    const next = NEXT_STATUS_AFTER[trip.status];
    if (!next) return;
    setTransitioning(true);
    try {
      // Best-effort GPS
      let gps: { lat?: number; lng?: number } = {};
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000 });
        });
        gps = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch { /* ignore */ }
      await apiClient.post('/api/v1/flatbed/trips/' + trip.id + '/transition', {
        newStatus: next, gpsLat: gps.lat, gpsLng: gps.lng,
      });
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setTransitioning(false);
    }
  }

  async function uploadDoc(file: File, docType: string) {
    if (!trip) return;
    const fd = new FormData();
    fd.append('documentType', docType);
    fd.append('file', file);
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
      });
      fd.append('gpsLat', String(pos.coords.latitude));
      fd.append('gpsLng', String(pos.coords.longitude));
    } catch { /* skip */ }
    const jwt = getStoredToken();
    const res = await fetch(API_BASE + '/api/v1/flatbed/trips/' + trip.id + '/documents', {
      method: 'POST',
      headers: jwt ? { Authorization: 'Bearer ' + jwt } : {},
      body: fd,
    });
    if (!res.ok) { window.alert('Upload failed: ' + res.statusText); return; }
    await refresh();
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !trip) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'Not found'}</div>
    </div>
  );

  const currentIdx = STAGES.indexOf(trip.status);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/flatbed"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Trips</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Truck className="h-4 w-4" /> {trip.trip_code}</div>
          {isAdmin ? (
            editing ? (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving}><X className="h-3.5 w-3.5" /> Cancel</Button>
                <Button size="sm" onClick={saveEdit} disabled={saving}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </Button>
              </div>
            ) : (
              <div className="flex gap-1">
                {/* PR-F4 MAY 11 — admin reassign */}
                <Button variant="outline" size="sm" onClick={() => setReassignOpen(true)} className="gap-1.5">Reassign</Button>
                <Button variant="outline" size="sm" onClick={startEdit} className="gap-1.5"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
              </div>
            )
          ) : (
            <div className="w-12" />
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {/* PR MAY10 P4B.2: Wasika banner (state-machine gate) */}
        <WasikaBanner flatbedTripId={trip.id} status={wasikaStatus} onOpen={() => setWasikaOpen(true)} />

        {/* Stage timeline */}
        <div className="rounded-lg border border-border p-3 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {STAGES.map((s, i) => {
              const passed = i <= currentIdx;
              const isCurrent = i === currentIdx;
              return (
                <div key={s} className="flex items-center gap-1">
                  <div className={'h-3 w-3 rounded-full ' + (isCurrent ? 'bg-foreground ring-4 ring-foreground/20' : passed ? 'bg-emerald-500' : 'bg-muted')}></div>
                  <div className="text-[10px] font-mono">{s}</div>
                  {i < STAGES.length - 1 && <div className={'h-px w-4 ' + (passed ? 'bg-emerald-500' : 'bg-border')}></div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action button */}
        {NEXT_STATUS_AFTER[trip.status] && (
          <Button onClick={transition} disabled={transitioning} className="gap-2 w-full sm:w-auto">
            {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Move to {NEXT_STATUS_AFTER[trip.status]}
          </Button>
        )}

        {editing && (
          <div className="rounded-lg border-2 border-primary bg-primary/5 p-4 space-y-4">
            <div className="text-sm font-medium text-foreground inline-flex items-center gap-2">
              <Pencil className="h-4 w-4" /> Editing trip {trip.trip_code}
            </div>

            <fieldset className="space-y-3">
              <legend className="text-xs uppercase tracking-widest text-muted-foreground">Route</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Pickup city *</Label><CityPicker value={(edit.pickupCity as string) ?? ''} onChange={(v) => setEditField('pickupCity', v)} /></div>
                <div className="space-y-1"><Label>Pickup location detail</Label><Input value={(edit.pickupLocationName as string) ?? ''} onChange={(e) => setEditField('pickupLocationName', e.target.value)} /></div>
                <div className="space-y-1"><Label>Delivery city *</Label><CityPicker value={(edit.deliveryCity as string) ?? ''} onChange={(v) => setEditField('deliveryCity', v)} /></div>
                <div className="space-y-1"><Label>Delivery location detail</Label><Input value={(edit.deliveryLocationName as string) ?? ''} onChange={(e) => setEditField('deliveryLocationName', e.target.value)} /></div>
                <div className="space-y-1"><Label>Expected distance (km)</Label><Input type="number" min={0} value={(edit.expectedDistanceKm as number) ?? ''} onChange={(e) => setEditField('expectedDistanceKm', e.target.value ? Number(e.target.value) : null)} /></div>
              </div>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-xs uppercase tracking-widest text-muted-foreground">Container & cargo</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Container type</Label>
                  <select value={(edit.containerType as string) ?? ''} onChange={(e) => setEditField('containerType', e.target.value || null)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                    <option value="">—</option>
                    {CONTAINER_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Container number</Label><Input value={(edit.containerNumber as string) ?? ''} onChange={(e) => setEditField('containerNumber', e.target.value)} /></div>
                <div className="space-y-1 md:col-span-2"><Label>Cargo description</Label>
                  <textarea value={(edit.cargoDescription as string) ?? ''} onChange={(e) => setEditField('cargoDescription', e.target.value)} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
                </div>
                <div className="space-y-1"><Label>Pickup ETA</Label><Input type="datetime-local" value={(edit.scheduledPickupAt as string) ?? ''} onChange={(e) => setEditField('scheduledPickupAt', e.target.value)} /></div>
                <div className="space-y-1"><Label>Delivery ETA</Label><Input type="datetime-local" value={(edit.scheduledDeliveryAt as string) ?? ''} onChange={(e) => setEditField('scheduledDeliveryAt', e.target.value)} /></div>
                <div className="space-y-1"><Label>Client reference</Label><Input value={(edit.clientReference as string) ?? ''} onChange={(e) => setEditField('clientReference', e.target.value)} /></div>
              </div>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-xs uppercase tracking-widest text-muted-foreground">Financials</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Trip rate (SAR) *</Label><Input type="number" step="0.01" min={0} value={(edit.rateSar as number) ?? ''} onChange={(e) => setEditField('rateSar', e.target.value ? Number(e.target.value) : 0)} /></div>
                <div className="space-y-1"><Label>Driver advance (SAR)</Label><Input type="number" step="0.01" min={0} value={(edit.driverAdvanceSar as number) ?? 0} onChange={(e) => setEditField('driverAdvanceSar', e.target.value ? Number(e.target.value) : 0)} /></div>
                <div className="space-y-1"><Label>Driver incentive (SAR)</Label><Input type="number" step="0.01" min={0} value={(edit.driverIncentiveSar as number) ?? ''} onChange={(e) => setEditField('driverIncentiveSar', e.target.value ? Number(e.target.value) : null)} /></div>
              </div>
            </fieldset>

            <fieldset className="space-y-3">
              <legend className="text-xs uppercase tracking-widest text-muted-foreground">Payment terms (P4A.3)</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Payment status</Label>
                  <select value={(edit.paymentStatus as string) ?? 'unset'} onChange={(e) => setEditField('paymentStatus', e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Payment method</Label>
                  <select value={(edit.paymentMethod as string) ?? ''} onChange={(e) => setEditField('paymentMethod', e.target.value || null)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                    <option value="">—</option>
                    {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Payer name</Label><Input value={(edit.payerName as string) ?? ''} onChange={(e) => setEditField('payerName', e.target.value)} /></div>
                <div className="space-y-1"><Label>Payer contact</Label><Input value={(edit.payerContact as string) ?? ''} onChange={(e) => setEditField('payerContact', e.target.value)} placeholder="+966…" /></div>
                <div className="space-y-1"><Label>Payment due date</Label><Input type="date" value={(edit.paymentDueDate as string) ?? ''} onChange={(e) => setEditField('paymentDueDate', e.target.value)} /></div>
                <div className="space-y-1"><Label>Amount paid so far (SAR)</Label><Input type="number" step="0.01" min={0} value={(edit.amountPaidSar as number) ?? 0} onChange={(e) => setEditField('amountPaidSar', e.target.value ? Number(e.target.value) : 0)} /></div>
                <div className="space-y-1 md:col-span-2"><Label>Payment notes</Label>
                  <textarea value={(edit.paymentNotes as string) ?? ''} onChange={(e) => setEditField('paymentNotes', e.target.value)} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
                </div>
              </div>
            </fieldset>

            <fieldset className="space-y-2">
              <legend className="text-xs uppercase tracking-widest text-muted-foreground">Internal notes</legend>
              <textarea value={(edit.internalNotes as string) ?? ''} onChange={(e) => setEditField('internalNotes', e.target.value)} rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
            </fieldset>
          </div>
        )}

        {!editing && (
        <div className="flex gap-2 border-b border-border overflow-x-auto">
          {([
            { v: 'overview',    l: 'Overview' },
            { v: 'documents',   l: 'Documents' },
            { v: 'financials',  l: 'Financials' },
            { v: 'analytics',   l: 'Analytics' },
            { v: 'activity',    l: 'Activity' },
            { v: 'anti_waste',  l: 'Anti-waste' },
          ] as const).map((x) => (
            <button key={x.v} onClick={() => setTab(x.v)}
              className={'px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ' + (tab === x.v ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {x.l}
            </button>
          ))}
        </div>
        )}

        {!editing && tab === 'overview' && (
          /* PR-D1 MAY 11 — every value is either a clickable EntityLink or an InlineEditField */
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Section title="Client" icon={<FileText className="h-3.5 w-3.5" />}>
              <InlineEditField
                label="Client"
                value={trip.client_id}
                renderValue={(id) => <EntityLink type="client" id={id} label={trip.client_name ?? undefined} />}
                editor={({ value, onChange }) => <EntityPicker kind="client" value={value} onChange={onChange} />}
                onSave={(v) => patchField('clientId', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Reference"
                value={trip.client_reference}
                editor={(p) => <TextEditor {...p} placeholder="Client reference / PO #" />}
                onSave={(v) => patchField('clientReference', v)}
                canEdit={isAdmin}
              />
            </Section>
            <Section title="Route" icon={<MapPin className="h-3.5 w-3.5" />}>
              <InlineEditField
                label="Pickup city"
                value={trip.pickup_city}
                editor={(p) => <TextEditor {...p} placeholder="Riyadh / Jeddah / …" />}
                onSave={(v) => patchField('pickupCity', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Pickup detail"
                value={trip.pickup_location_name}
                editor={(p) => <TextEditor {...p} placeholder="Address / landmark" />}
                onSave={(v) => patchField('pickupLocationName', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Delivery city"
                value={trip.delivery_city}
                editor={(p) => <TextEditor {...p} placeholder="Riyadh / Jeddah / …" />}
                onSave={(v) => patchField('deliveryCity', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Delivery detail"
                value={trip.delivery_location_name}
                editor={(p) => <TextEditor {...p} placeholder="Address / landmark" />}
                onSave={(v) => patchField('deliveryLocationName', v)}
                canEdit={isAdmin}
              />
              <InlineEditField<number>
                label="Distance (km)"
                value={trip.expected_distance_km}
                renderValue={(v) => v + ' km'}
                editor={(p) => <NumberEditor {...p} min={0} step={1} />}
                onSave={(v) => patchField('expectedDistanceKm', v)}
                canEdit={isAdmin}
              />
            </Section>
            <Section title="Container" icon={<Truck className="h-3.5 w-3.5" />}>
              <InlineEditField
                label="Type"
                value={trip.container_type}
                editor={(p) => <SelectEditor {...p} options={CONTAINER_TYPES.map((t) => ({ value: t, label: t }))} />}
                onSave={(v) => patchField('containerType', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Number"
                value={trip.container_number}
                editor={(p) => <TextEditor {...p} placeholder="ABCU1234567" />}
                onSave={(v) => patchField('containerNumber', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Cargo"
                value={trip.cargo_description}
                editor={(p) => <TextEditor {...p} placeholder="Steel pipes, machinery, …" />}
                onSave={(v) => patchField('cargoDescription', v)}
                canEdit={isAdmin}
              />
            </Section>
            <Section title="Assignment" icon={<Truck className="h-3.5 w-3.5" />}>
              <InlineEditField
                label="Vehicle"
                value={trip.vehicle_id}
                renderValue={(id) => <EntityLink type="vehicle" id={id} label={trip.vehicle_plate ?? undefined} />}
                editor={({ value, onChange }) => <EntityPicker kind="vehicle" value={value} onChange={onChange} />}
                onSave={(v) => patchField('vehicleId', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Driver"
                value={trip.driver_id}
                renderValue={(id) => (
                  <span className="inline-flex items-center gap-2 flex-wrap">
                    <EntityLink type="driver" id={id} label={trip.driver_name ?? undefined} />
                    {trip.driver_phone && (
                      <a href={'tel:' + trip.driver_phone} className="text-xs text-muted-foreground underline-offset-2 hover:underline">{trip.driver_phone}</a>
                    )}
                  </span>
                )}
                editor={({ value, onChange }) => <EntityPicker kind="driver" value={value} onChange={onChange} />}
                onSave={(v) => patchField('driverId', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Pickup ETA"
                value={trip.scheduled_pickup_at}
                renderValue={(v) => new Date(v).toLocaleString('en-GB')}
                editor={(p) => <DateTimeEditor {...p} />}
                onSave={(v) => patchField('scheduledPickupAt', v)}
                canEdit={isAdmin}
              />
              <InlineEditField
                label="Delivery ETA"
                value={trip.scheduled_delivery_at}
                renderValue={(v) => new Date(v).toLocaleString('en-GB')}
                editor={(p) => <DateTimeEditor {...p} />}
                onSave={(v) => patchField('scheduledDeliveryAt', v)}
                canEdit={isAdmin}
              />
            </Section>
          </div>
        )}

        {!editing && tab === 'documents' && (
          <div className="space-y-4">
            {/* PR-D3 MAY 11 — Fuel & Distance section (driver/admin records numbers here) */}
            <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Fuel & Distance</div>
                {trip.consumption_l_per_100km && Number(trip.consumption_l_per_100km) > 0 && (
                  <span className={'text-xs px-2 py-0.5 rounded-full ' + (Number(trip.consumption_l_per_100km) > 35 ? 'bg-rose-500/10 text-rose-700 dark:text-rose-400' : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400')}>
                    {Number(trip.consumption_l_per_100km).toFixed(1)} L / 100 km
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">At pickup</div>
                  <InlineEditField<number>
                    label="Odometer (km)"
                    value={trip.odometer_at_pickup_km}
                    renderValue={(v) => v.toLocaleString('en-US') + ' km'}
                    editor={(p) => <NumberEditor {...p} min={0} step={1} />}
                    onSave={(v) => patchField('odometerAtPickupKm', v)}
                    canEdit={isAdmin}
                  />
                  <InlineEditField<number>
                    label="Fuel filled (L)"
                    value={trip.fuel_filled_at_pickup_liters ? Number(trip.fuel_filled_at_pickup_liters) : null}
                    renderValue={(v) => v.toFixed(2) + ' L'}
                    editor={(p) => <NumberEditor {...p} min={0} step={0.01} />}
                    onSave={(v) => patchField('fuelFilledAtPickupLiters', v)}
                    canEdit={isAdmin}
                  />
                  <InlineEditField<number>
                    label="Fuel price (SAR/L)"
                    value={trip.fuel_price_at_pickup_sar ? Number(trip.fuel_price_at_pickup_sar) : null}
                    renderValue={(v) => v.toFixed(3) + ' SAR'}
                    editor={(p) => <NumberEditor {...p} min={0} step={0.001} />}
                    onSave={(v) => patchField('fuelPriceAtPickupSar', v)}
                    canEdit={isAdmin}
                  />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">At delivery</div>
                  <InlineEditField<number>
                    label="Odometer (km)"
                    value={trip.odometer_at_delivery_km}
                    renderValue={(v) => v.toLocaleString('en-US') + ' km'}
                    editor={(p) => <NumberEditor {...p} min={0} step={1} />}
                    onSave={(v) => patchField('odometerAtDeliveryKm', v)}
                    canEdit={isAdmin}
                  />
                  <InlineEditField<number>
                    label="Top-up (L)"
                    value={trip.fuel_topped_up_liters ? Number(trip.fuel_topped_up_liters) : null}
                    renderValue={(v) => v.toFixed(2) + ' L'}
                    editor={(p) => <NumberEditor {...p} min={0} step={0.01} />}
                    onSave={(v) => patchField('fuelToppedUpLiters', v)}
                    canEdit={isAdmin}
                  />
                  <InlineEditField<number>
                    label="Top-up price (SAR/L)"
                    value={trip.fuel_price_at_delivery_sar ? Number(trip.fuel_price_at_delivery_sar) : null}
                    renderValue={(v) => v.toFixed(3) + ' SAR'}
                    editor={(p) => <NumberEditor {...p} min={0} step={0.001} />}
                    onSave={(v) => patchField('fuelPriceAtDeliverySar', v)}
                    canEdit={isAdmin}
                  />
                </div>
              </div>
              {(trip.distance_km || trip.total_fuel_cost_sar) && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center pt-3 border-t border-border">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Distance</div>
                    <div className="text-sm font-medium">{trip.distance_km ? trip.distance_km.toLocaleString('en-US') + ' km' : '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fuel total</div>
                    <div className="text-sm font-medium">{trip.total_fuel_liters ? Number(trip.total_fuel_liters).toFixed(1) + ' L' : '—'}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fuel cost</div>
                    <div className="text-sm font-medium">{trip.total_fuel_cost_sar ? fmtSar(Number(trip.total_fuel_cost_sar)) : '—'}</div>
                  </div>
                </div>
              )}
            </div>

            {/* PR-S1 MAY 11 — new column-based slots (pickup truck/receipt, wasika, delivery truck/doc) */}
            <DocumentsTab
              tripId={trip.id}
              status={trip.status}
              isAdmin={isAdmin}
              pickupTruckUrl={trip.pickup_truck_photo_url ?? null}
              pickupReceiptUrl={trip.pickup_receipt_photo_url ?? null}
              wasikaUrl={trip.wasika_pdf_url ?? null}
              deliveryTruckUrl={trip.delivery_truck_photo_url ?? null}
              deliveryDocumentUrl={trip.delivery_document_photo_url ?? null}
              pickupUploadedAt={trip.pickup_photos_uploaded_at ?? null}
              wasikaUploadedAt={trip.wasika_uploaded_at ?? null}
              deliveryUploadedAt={trip.delivery_photos_uploaded_at ?? null}
              forceAuditCount={Array.isArray(trip.force_advance_audit) ? trip.force_advance_audit.length : 0}
              onChange={refresh}
            />

            {/* Legacy document grid (flatbed_trip_documents — kept for backwards compat) */}
            <details className="rounded-lg border border-border">
              <summary className="cursor-pointer px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
                Other documents ({docs.length})
              </summary>
              <div className="p-3 space-y-3">
                <UploadSection onUpload={uploadDoc} />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {docs.map((d) => (
                    <a key={d.id} href={API_BASE + '/api/v1/flatbed/trips/' + trip.id + '/documents/' + d.id + '/file'} target="_blank" rel="noreferrer"
                      className="block rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors">
                      <div className="text-xs font-mono">{d.document_type}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">{d.mime_type ?? ''}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(d.uploaded_at).toLocaleString('en-GB')}</div>
                    </a>
                  ))}
                  {docs.length === 0 && <div className="col-span-full text-sm text-muted-foreground text-center py-4">No legacy documents.</div>}
                </div>
              </div>
            </details>
          </div>
        )}

        {!editing && tab === 'financials' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Trip economics</div>
              <Row label="Rate" value={fmtSar(Number(trip.rate_sar))} />
              <Row label="Driver advance" value={fmtSar(Number(trip.driver_advance_sar ?? 0))} />
              <Row label="Driver incentive" value={trip.driver_incentive_sar ? fmtSar(Number(trip.driver_incentive_sar)) + (trip.driver_incentive_released ? ' (released)' : ' (pending payment)') : '—'} />
            </div>
            <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Payment terms</div>
              <Row label="Status"        value={(trip.payment_status ?? 'unset').replace(/_/g, ' ')} />
              <Row label="Payer"         value={trip.payer_name ?? '—'} />
              <Row label="Payer contact" value={trip.payer_contact ?? '—'} />
              <Row label="Method"        value={(trip.payment_method ?? '—').replace(/_/g, ' ')} />
              <Row label="Due date"      value={trip.payment_due_date ?? '—'} />
              <Row label="Amount paid"   value={fmtSar(Number(trip.amount_paid_sar ?? 0))} />
              <Row label="Outstanding"   value={fmtSar(Math.max(0, Number(trip.rate_sar) - Number(trip.amount_paid_sar ?? 0)))} />
              {trip.payment_notes && <Row label="Notes" value={trip.payment_notes} />}
            </div>
            <div className="rounded-lg border border-border p-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Collections</div>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setCollectionOpen(true)} className="gap-1">
                    + Record collection
                  </Button>
                )}
              </div>
              {collections.length === 0 ? (
                <div className="text-xs text-muted-foreground italic">No collections recorded yet.</div>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="text-muted-foreground border-b border-border">
                    <tr>
                      <th className="text-left py-1 pr-2">Date</th>
                      <th className="text-right py-1 pr-2">Amount</th>
                      <th className="text-left py-1 pr-2">Method</th>
                      <th className="text-left py-1 pr-2">Collector</th>
                      <th className="text-left py-1 pr-2">Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collections.map((c) => (
                      <tr key={c.id} className="border-b border-border last:border-b-0">
                        <td className="py-1 pr-2">{new Date(c.collected_at).toLocaleDateString('en-GB')}</td>
                        <td className="py-1 pr-2 text-right font-mono">{fmtSar(Number(c.amount_sar))}</td>
                        <td className="py-1 pr-2">{c.payment_method.replace(/_/g, ' ')}</td>
                        <td className="py-1 pr-2">{c.collected_by_user_name ?? c.collected_by_driver_name ?? '—'}</td>
                        <td className="py-1 pr-2">{c.verified_at ? '✓' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="rounded-lg border border-border p-4 space-y-2 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Invoicing (legacy)</div>
              <Row label="Invoice number"  value={trip.invoice_number ?? '—'} />
              <Row label="Invoiced at"     value={trip.invoiced_at ? new Date(trip.invoiced_at).toLocaleString('en-GB') : '—'} />
              <Row label="Expected payment" value={trip.expected_payment_date ?? '—'} />
              <Row label="Paid at"          value={trip.paid_at ? new Date(trip.paid_at).toLocaleString('en-GB') : '—'} />
              <Row label="Amount received"  value={trip.payment_received_sar ? fmtSar(Number(trip.payment_received_sar)) : '—'} />
            </div>
          </div>
        )}

        {/* PR-D4 MAY 11 — per-trip P&L analytics */}
        {!editing && tab === 'analytics' && (
          <AnalyticsTab tripId={trip.id} />
        )}

        {!editing && tab === 'activity' && (
          <div className="rounded-lg border border-border">
            {stages.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center">No stage history.</div>
            ) : stages.map((s) => (
              <div key={s.id} className="px-4 py-2 border-t border-border first:border-t-0 text-sm flex items-baseline gap-3">
                <span className="font-mono text-xs text-muted-foreground w-32">{new Date(s.changed_at).toLocaleString('en-GB')}</span>
                <Activity className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{s.from_status ?? '∅'} → <strong>{s.to_status}</strong></span>
                {s.notes && <span className="text-xs text-muted-foreground">· {s.notes}</span>}
              </div>
            ))}
          </div>
        )}

        {!editing && tab === 'anti_waste' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">Long-haul anti-waste analysis (consumption · idle · route deviation)</div>
              <Button size="sm" variant="outline" onClick={runWasteAnalysis} disabled={analyzing} className="gap-2 shrink-0">
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                {waste && (waste.consumption || waste.idle || waste.route) ? 'Re-run' : 'Run analysis'}
              </Button>
            </div>

            {wasteLoading && (
              <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
            )}

            {!wasteLoading && waste && !waste.consumption && !waste.idle && !waste.route && (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No analysis yet for this trip. Click <strong>Run analysis</strong> (admin only) to compute.
              </div>
            )}

            {!wasteLoading && waste && (waste.consumption || waste.idle || waste.route) && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {waste.consumption && (
                  <Section title="Fuel consumption" icon={<Activity className="h-3.5 w-3.5" />}>
                    <Row label="Status"        value={String(waste.consumption.status ?? '—')} />
                    <Row label="Expected (km)" value={String(waste.consumption.expected_distance_km ?? '—')} />
                    <Row label="Actual (km)"   value={String(waste.consumption.actual_distance_km ?? '—')} />
                    <Row label="Expected (L)"  value={String(waste.consumption.expected_liters ?? '—')} />
                    <Row label="Actual (L)"    value={String(waste.consumption.actual_liters ?? '—')} />
                    <Row label="Variance %"    value={waste.consumption.variance_pct != null ? Number(waste.consumption.variance_pct).toFixed(1) + '%' : '—'} />
                    <Row label="Waste cost"    value={fmtSar(Number(waste.consumption.waste_cost_sar ?? 0))} />
                  </Section>
                )}
                {waste.idle && (
                  <Section title="Idle time" icon={<Activity className="h-3.5 w-3.5" />}>
                    <Row label="Engine on (min)"     value={String(waste.idle.total_engine_on_minutes ?? '—')} />
                    <Row label="Total idle (min)"    value={String(waste.idle.total_idle_minutes ?? '—')} />
                    <Row label="Meal (min)"          value={String(waste.idle.meal_idle_minutes ?? 0)} />
                    <Row label="Prayer (min)"        value={String(waste.idle.prayer_idle_minutes ?? 0)} />
                    <Row label="Border (min)"        value={String(waste.idle.border_idle_minutes ?? 0)} />
                    <Row label="Rest (min)"          value={String(waste.idle.rest_idle_minutes ?? 0)} />
                    <Row label="Unexplained (min)"   value={String(waste.idle.unexplained_idle_minutes ?? 0)} />
                    <Row label="Unexplained cost"    value={fmtSar(Number(waste.idle.unexplained_idle_cost_sar ?? 0))} />
                  </Section>
                )}
                {waste.route && (
                  <Section title="Route deviation" icon={<MapPin className="h-3.5 w-3.5" />}>
                    <Row label="Status"             value={String(waste.route.status ?? '—')} />
                    <Row label="Expected (km)"      value={String(waste.route.expected_route_distance_km ?? '—')} />
                    <Row label="Actual (km)"        value={String(waste.route.actual_route_distance_km ?? '—')} />
                    <Row label="Deviation (km)"     value={String(waste.route.deviation_km ?? '—')} />
                    <Row label="Deviation %"        value={waste.route.deviation_pct != null ? Number(waste.route.deviation_pct).toFixed(1) + '%' : '—'} />
                    <Row label="Max off-route (km)" value={String(waste.route.max_off_route_distance_km ?? '—')} />
                  </Section>
                )}
              </div>
            )}

            {waste && waste.anomalies && waste.anomalies.length > 0 && (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="px-4 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
                  Anomalies ({waste.anomalies.length})
                </div>
                {waste.anomalies.map((a) => (
                  <div key={a.id} className="px-4 py-2 border-t border-border first:border-t-0 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (a.severity === 'critical' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400' : a.severity === 'high' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400' : 'bg-muted text-muted-foreground')}>
                        {a.severity}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">{a.anomaly_type}</span>
                      <span className="font-medium">{a.title}</span>
                      {a.impact_sar != null && <span className="ml-auto font-mono text-xs">{fmtSar(Number(a.impact_sar))}</span>}
                    </div>
                    {a.description && <div className="text-xs text-muted-foreground mt-1">{a.description}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(a.detected_at).toLocaleString('en-GB')} · {a.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <WasikaModal open={wasikaOpen} onOpenChange={setWasikaOpen} flatbedTripId={trip.id} onUpdated={refreshWasika} />
      <RecordCollectionModal open={collectionOpen} onOpenChange={setCollectionOpen} flatbedTripId={trip.id}
        onRecorded={() => { refreshCollections(); refresh(); }} />
      <ReassignSheet open={reassignOpen} onOpenChange={setReassignOpen} tripId={trip.id}
        currentVehicleId={null} currentDriverId={null}
        onReassigned={refresh} />
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5 mb-2">{icon} {title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between text-sm gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

// PR-D4 MAY 11 — per-trip P&L analytics tab
interface AnalyticsBundle {
  tripCode: string;
  route: string;
  status: string;
  revenue: { clientPaymentSar: number; collectedSar: number; outstandingSar: number; paymentState: string };
  directCosts: { driverPaySar: number; fuelCostSar: number; fuelLiters: number; distanceKm: number; consumptionL100km: number | null };
  indirectCosts: { maintenanceAmortizedSar: number; depreciationSar: number; tollsAndPermitsSar: number };
  summary: { totalCostSar: number; grossProfitSar: number; grossMarginPct: number; costPerKmSar: number | null; profitPerKmSar: number | null };
  warnings: Array<{ type: string; message: string }>;
}

function AnalyticsTab({ tripId }: { tripId: string }) {
  const [data, setData] = useState<AnalyticsBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get<AnalyticsBundle>('/api/v1/flatbed/trips/' + tripId + '/analytics')
      .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch((e) => { if (!cancelled) setError(extractErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tripId]);
  if (loading) return <div className="rounded-lg border border-border p-6 text-center text-sm text-muted-foreground">Loading analytics…</div>;
  if (error)   return <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>;
  if (!data)   return null;
  const profitClass = data.summary.grossProfitSar < 0 ? 'text-rose-700 dark:text-rose-400' : 'text-emerald-700 dark:text-emerald-400';
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Revenue</div>
          <div className="text-2xl font-light mt-1">{fmtSar(data.revenue.clientPaymentSar)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Collected {fmtSar(data.revenue.collectedSar)} · Outstanding {fmtSar(data.revenue.outstandingSar)}</div>
        </div>
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Direct costs</div>
          <div className="text-2xl font-light mt-1">{fmtSar(data.directCosts.driverPaySar + data.directCosts.fuelCostSar)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Driver {fmtSar(data.directCosts.driverPaySar)} · Fuel {fmtSar(data.directCosts.fuelCostSar)}</div>
        </div>
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Indirect costs</div>
          <div className="text-2xl font-light mt-1">{fmtSar(data.indirectCosts.maintenanceAmortizedSar)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Maint. amortized (last 30d / km)</div>
        </div>
        <div className="rounded-lg border-2 border-foreground bg-muted/10 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Net profit</div>
          <div className={'text-2xl font-medium mt-1 ' + profitClass}>{fmtSar(data.summary.grossProfitSar)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Margin {data.summary.grossMarginPct.toFixed(2)}% · {data.summary.costPerKmSar != null ? 'Cost ' + fmtSar(data.summary.costPerKmSar) + '/km' : ''}</div>
        </div>
      </div>
      <div className="rounded-lg border border-border p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Distance</div><div>{data.directCosts.distanceKm ? data.directCosts.distanceKm.toLocaleString('en-US') + ' km' : '—'}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Fuel total</div><div>{data.directCosts.fuelLiters ? data.directCosts.fuelLiters.toFixed(1) + ' L' : '—'}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Consumption</div><div>{data.directCosts.consumptionL100km != null ? data.directCosts.consumptionL100km.toFixed(1) + ' L/100km' : '—'}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Profit / km</div><div>{data.summary.profitPerKmSar != null ? fmtSar(data.summary.profitPerKmSar) : '—'}</div></div>
      </div>
      {data.warnings.length > 0 && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Warnings</div>
          {data.warnings.map((w) => (
            <div key={w.type} className="text-sm text-rose-700 dark:text-rose-400 inline-flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" /> {w.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UploadSection({ onUpload }: { onUpload: (file: File, docType: string) => void }) {
  const [docType, setDocType] = useState('pickup_photo');
  return (
    <div className="rounded-lg border border-border p-3 flex items-end gap-2 flex-wrap">
      <div className="space-y-1 flex-1 min-w-[10rem]">
        <Label className="text-xs">Document type</Label>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
          {['consignment_note','pickup_photo','shipper_receipt','loaded_truck_photo','delivery_photo','signed_pod','toll_receipt','parking_receipt','fuel_receipt','damage_photo','customs_doc','other'].map((d) => (
            <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>
      <Input type="file" accept="image/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f, docType); }} className="flex-1 min-w-[14rem]" />
      <Camera className="h-4 w-4 text-muted-foreground" />
    </div>
  );
}

